/**
 * Discipline Service - Unified exports for discipline system
 *
 * This file provides a simplified export interface to the modular discipline services.
 * All business logic has been moved to specialized services in ./discipline/
 */

import { arrayUnion, Timestamp } from 'firebase/firestore';

// Import repositories
import { MentionRepository } from './discipline/repositories/MentionRepository';
import { ConsequenceRepository } from './discipline/repositories/ConsequenceRepository';
import { OffenceRepository } from './discipline/repositories/OffenceRepository';
import { RuleRepository } from './discipline/repositories/RuleRepository';
import { ValueRepository } from './discipline/repositories/ValueRepository';

// Import core services
import { MentionService } from './discipline/core/MentionService';
import { ConsequenceService } from './discipline/core/ConsequenceService';
import { QueueService } from './discipline/core/QueueService';
import { OffenceService } from './discipline/core/OffenceService';
import { ValueService } from './discipline/core/ValueService';
import { RuleService } from './discipline/core/RuleService';
import { TimelineService } from './discipline/core/TimelineService';
import { CategoryService } from './discipline/core/CategoryService';

// Import rule evaluator
import { RuleEvaluator } from './discipline/rules/RuleEvaluator';
import { studentDataFetcher } from './discipline/rules/StudentDataFetcher';
import { disciplineLogger } from '../utils/disciplineLogger';
import { processingGuard } from './discipline/utils/ProcessingGuard';

// Import points service for violations deduction
import { pointsService } from './recognitionService';

// Re-export constants for backward compatibility
export { DISCIPLINE_TYPES } from './discipline/constants';

// Initialize repositories
const mentionRepo = new MentionRepository();
const consequenceRepo = new ConsequenceRepository();
const offenceRepo = new OffenceRepository();
const ruleRepo = new RuleRepository();
const valueRepo = new ValueRepository();

// Initialize and export service instances
export const mentionService = new MentionService(mentionRepo);
export const consequenceService = new ConsequenceService(consequenceRepo);
export const queueService = new QueueService(consequenceRepo);
export const offenceService = new OffenceService(offenceRepo);
export const valueService = new ValueService(valueRepo);
export const ruleService = new RuleService(ruleRepo);
export const timelineService = new TimelineService();
export const categoryService = new CategoryService();

// Initialize rule evaluator
export const ruleEvaluator = new RuleEvaluator({
  mentionService,
  ruleService,
  consequenceService,
  contractService: null // TODO: Add ContractService when implemented
});

// Wire up circular dependency: ConsequenceService needs RuleEvaluator for threshold checking
consequenceService.setRuleEvaluator(ruleEvaluator);

// For backward compatibility with old code that expects disciplineService object
// This allows gradual migration: consumers can switch one service at a time
export const disciplineService = {
  // Mention operations
  issueMention: async (...args) => {
    // Auto-resolve houseId if not provided
    const mentionData = args[0];
    if (!mentionData.houseId && mentionData.studentId && mentionData.schoolId) {
      try {
        const { resolveHouseForStudent } = await import('../utils/houseResolver');
        const resolved = await resolveHouseForStudent(mentionData.studentId, mentionData.schoolId);
        mentionData.houseId = resolved.houseId;
        mentionData.houseName = resolved.houseName;
      } catch (houseErr) {
        console.warn('[DisciplineService] House resolution failed (non-blocking):', houseErr.message);
      }
    }

    disciplineLogger.trace('Creating mention', args[0], 'mentions');
    const mention = await mentionService.issueMention(...args);
    disciplineLogger.trace(`Mention created, processing rules: ${mention.id}`, null, 'mentions');
    if (
      mention.wasCreated === false &&
      mention.ruleProcessingStatus === 'complete'
    ) {
      mention.createdConsequences = [];
      return mention;
    }

    // Check for potential loops (monitoring only, doesn't block)
    processingGuard.checkMentionProcessing(mention.id, 'rules');

    try {
      const ruleResult = await ruleEvaluator.processRulesForMention(mention);
      mention.createdConsequences = ruleResult?.consequences || [];
      await mentionService.updateMention(mention.id, {
        schoolId: mention.schoolId,
        ruleProcessingStatus: 'complete',
        ruleProcessingError: null,
        ruleProcessingFailedAt: null,
        ruleProcessingCompletedAt: Timestamp.now(),
        // Accountability persists its response and source links together.
        // Completing this pass must not replace links saved by another family.
        ...(mention.createdConsequences.length ? {
          triggeredConsequences: arrayUnion(...mention.createdConsequences.map(consequence => consequence.id)),
        } : {}),
        updatedAt: Timestamp.now(),
      });
      disciplineLogger.trace('Rules processed successfully', null, 'mentions');

      // CRITICAL: Invalidate mentions cache AFTER rule processing completes
      // This allows rules to use cached data during processing, then clears it for next time
      studentDataFetcher.invalidateMentionsCache(mention.studentId);
      disciplineLogger.debug('✓ Mentions cache invalidated after rule processing', {
        studentId: mention.studentId,
        mentionId: mention.id
      });

      // Check if points deduction is enabled for violations
      // TODO: Get this from actual school settings once implemented
      const schoolConfig = {
        recognition: {
          violations: {
            deductPoints: true, // This would come from school settings
            defaultDeduction: 5
          }
        }
      };

      // Deduct points if configured
      if (
        mention.wasCreated !== false &&
        schoolConfig.recognition?.violations?.deductPoints &&
        mention.schoolId
      ) {
        try {
          // Deduct points - works with or without house assignment
          await pointsService.deductPoints({
            studentId: mention.studentId,
            studentName: mention.studentName,
            classId: mention.classId,
            className: mention.className,
            houseId: mention.houseId || null, // Can be null
            houseName: mention.houseName || null,
            amount: schoolConfig.recognition.violations.defaultDeduction,
            type: 'violation',
            sourceId: mention.id,
            valueId: mention.offenseCategoryId || null,
            valueName: mention.offenseCategory || mention.offence,
            schoolId: mention.schoolId
          });

          if (!mention.houseId) {
            disciplineLogger.debug('⚠️ Points deducted for student without house (pending assignment)', {
              studentId: mention.studentId,
              mentionId: mention.id,
              amount: schoolConfig.recognition.violations.defaultDeduction
            });
          } else {
            disciplineLogger.debug('✓ Points deducted for violation', {
              studentId: mention.studentId,
              mentionId: mention.id,
              amount: schoolConfig.recognition.violations.defaultDeduction,
              house: mention.houseName
            });
          }
        } catch (pointsError) {
          // Log error but don't fail the mention creation
          console.error('Failed to deduct points for violation:', pointsError);
          disciplineLogger.debug('✗ Failed to deduct points', {
            error: pointsError.message,
            mentionId: mention.id
          });
        }
      }
    } catch (error) {
      console.error('ERROR processing rules', error);
      await mentionService.updateMention(mention.id, {
        schoolId: mention.schoolId,
        ruleProcessingStatus: 'failed',
        ruleProcessingError: error.message,
        ruleProcessingFailedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      }).catch(updateError => {
        console.error('Failed to record Mention rule-processing failure', updateError);
      });
      processingGuard.completeMentionProcessing(mention.id, 'rules');
      throw error;
    }
    return mention;
  },
  getMentionsByStudent: (...args) => mentionService.getMentionsByStudent(...args),
  getMentionsByClass: (...args) => mentionService.getMentionsByClass(...args),
  getTodaysMentions: (...args) => mentionService.getTodaysMentions(...args),
  subscribeTodaysMentions: (...args) => mentionService.subscribeTodaysMentions(...args),
  updateMention: (...args) => mentionService.updateMention(...args),
  deleteMention: async (mentionId, schoolId) => {
    // Fetch mention data before deletion so we can reverse points
    let mentionData = null;
    try {
      mentionData = await mentionRepo.getById(mentionId, schoolId);
    } catch (err) {
      console.warn('Could not fetch mention before deletion (points may not be reversed):', err.message);
    }

    // Delete the mention
    await mentionService.deleteMention(mentionId, schoolId);

    // Reverse the points deduction (non-blocking — don't fail the delete)
    if (mentionData && mentionData.schoolId) {
      try {
        await pointsService.reverseViolationPoints({
          studentId: mentionData.studentId,
          studentName: mentionData.studentName,
          classId: mentionData.classId,
          className: mentionData.className,
          houseId: mentionData.houseId || null,
          houseName: mentionData.houseName || null,
          amount: 1, // Same default as deduction
          sourceId: mentionId,
          valueId: mentionData.offenseCategoryId || null,
          valueName: mentionData.offenseCategory || mentionData.offence,
          schoolId: mentionData.schoolId
        });
        disciplineLogger.debug('✓ Points reversed for deleted mention', {
          mentionId,
          studentId: mentionData.studentId
        });
      } catch (pointsError) {
        console.error('Failed to reverse points for deleted mention:', pointsError);
      }
    }
  },

  // Consequence operations
  createDisciplineConsequence: (...args) => consequenceService.createDisciplineConsequence(...args),

  // Offence operations
  createOffence: (...args) => offenceService.createOffence(...args),
  getOffences: (schoolId, ...args) => offenceService.getOffences(schoolId, ...args),
  subscribeOffences: (schoolId, callback, ...args) => offenceService.subscribeOffences(schoolId, callback, ...args),
  updateOffence: (...args) => offenceService.updateOffence(...args),
  deleteOffence: (...args) => offenceService.deleteOffence(...args),

  // Monitoring operations (only available in dev mode)
  getProcessingMetrics: () => {
    if (import.meta.env.DEV) {
      return processingGuard.getMetrics();
    }
    return null;
  },
  resetProcessingGuard: () => {
    if (import.meta.env.DEV) {
      processingGuard.reset();
      console.log('[DisciplineService] Processing guard reset');
    }
  },

  // Value operations
  createValue: (...args) => valueService.createValue(...args),
  getValues: (...args) => valueService.getValues(...args),
  updateValue: (...args) => valueService.updateValue(...args),
  deleteValue: (...args) => valueService.deleteValue(...args),
  updateValueSortOrders: (...args) => valueService.updateValueSortOrders(...args),

  // Category operations
  createDisciplineCategory: (...args) => categoryService.createCategory(...args),
  getDisciplineCategories: (...args) => categoryService.getCategories(...args),
  updateDisciplineCategory: (...args) => categoryService.updateCategory(...args),
  deleteDisciplineCategory: (...args) => categoryService.deleteCategory(...args),
  subscribeDisciplineCategories: (...args) => categoryService.subscribeCategories(...args),

  // Rule operations
  createDisciplineRule: (...args) => ruleService.createRule(...args),
  getDisciplineRules: (...args) => ruleService.getActiveRules(...args),
  updateDisciplineRule: (...args) => ruleService.updateRule(...args),
  deleteDisciplineRule: (...args) => ruleService.deleteRule(...args),
  updateRuleSortOrders: (...args) => ruleService.updateSortOrders(...args),
  subscribeDisciplineRules: (...args) => ruleService.subscribeRules(...args),

  // Queue operations
  addToQueue: (...args) => queueService.addToQueue(...args),
  getQueueItems: (...args) => queueService.getQueueItems(...args),
  updateQueueItem: (...args) => queueService.updateQueueItem(...args),
  actionQueueItem: (...args) => queueService.actionQueueItem(...args),
  updateQueueItems: (...args) => queueService.updateQueueItems(...args),
  deleteQueueItem: (itemId, schoolId) => queueService.deleteQueueItem(itemId, schoolId),
  undoQueueItem: (...args) => queueService.undoQueueItem(...args),
  markDetentionServed: (...args) => queueService.markDetentionServed(...args),
  scheduleDetention: (...args) => queueService.scheduleDetention(...args),
  getQueueStatistics: (...args) => queueService.getQueueStatistics(...args),

  // Timeline operations
  getTimelineEvents: (...args) => timelineService.getTimelineEvents(...args),
  applyTimelineFilters: (...args) => timelineService.applyTimelineFilters(...args),
  getTimelineStats: (...args) => timelineService.getTimelineStats(...args),

  // Dev/test operations
  purgeSampleClassData: async (schoolId) => {
    const { collection, getDocs, deleteDoc, doc } = await import('firebase/firestore');
    const { db } = await import('../firebase');

    console.log('🗑️ Purging sample class data...');
    let deletedCount = 0;

    try {
      if (!schoolId) {
        throw new Error('schoolId is required for purging sample class data');
      }

      // HIERARCHICAL PATH: schools/{schoolId}/mentions
      const mentionsRef = collection(db, 'schools', schoolId, 'mentions');
      const mentionsSnapshot = await getDocs(mentionsRef);
      const sampleMentions = mentionsSnapshot.docs.filter(d =>
        d.data().studentId?.startsWith('sample-class')
      );
      console.log(`Found ${sampleMentions.length} sample class mentions to delete`);

      for (const docSnap of sampleMentions) {
        await deleteDoc(doc(db, 'schools', schoolId, 'mentions', docSnap.id));
        deletedCount++;
      }

      // HIERARCHICAL PATH: schools/{schoolId}/disciplineQueue
      const queueRef = collection(db, 'schools', schoolId, 'disciplineQueue');
      const queueSnapshot = await getDocs(queueRef);
      const sampleQueue = queueSnapshot.docs.filter(d =>
        d.data().studentId?.startsWith('sample-class')
      );
      console.log(`Found ${sampleQueue.length} sample class discipline queue items to delete`);

      for (const docSnap of sampleQueue) {
        await deleteDoc(doc(db, 'schools', schoolId, 'disciplineQueue', docSnap.id));
        deletedCount++;
      }

      // HIERARCHICAL PATH: schools/{schoolId}/studentCounters
      const countersRef = collection(db, 'schools', schoolId, 'studentCounters');
      const countersSnapshot = await getDocs(countersRef);
      const sampleCounters = countersSnapshot.docs.filter(d =>
        d.id?.startsWith('sample-class')
      );
      console.log(`Found ${sampleCounters.length} sample class counter documents to delete`);

      for (const docSnap of sampleCounters) {
        await deleteDoc(doc(db, 'schools', schoolId, 'studentCounters', docSnap.id));
        deletedCount++;
      }

      console.log(`✅ Purge complete: deleted ${deletedCount} documents`);
      return { success: true, deletedCount };
    } catch (error) {
      console.error('Error purging sample class data:', error);
      throw error;
    }
  }
};
